import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import Modal from 'react-modal';
import { Helmet } from 'react-helmet';
import moment from 'moment-timezone';
import axios from 'axios';
// import * as Location from 'expo-location';
import { DatePicker } from 'react-rainbow-components';
import MechanicEntryForm from './mechanic-entry-form';
import TrainingEnforcementModal from '../elements/TrainingEnforcementModal';
import MechanicProfileBasicsModal from '../elements/MechanicProfileBasicsModal';
import { CANCELLED_STATUSES, TERMINAL_STATUSES, IN_PROGRESS_STATUSES } from "../../constants/statuses";
import { deleteLocalItem, getLocalItem } from '../../utils/storage';
import SpareScanCard from './spare-scan-card'; // IMPL_07: scan spare QR -> ₹30 credit

// Defensive auth-header helper. Reads the mechanic JWT from localStorage and
// returns an axios `headers` object with Authorization: Bearer <jwt>. Returns
// an empty object if the token is missing (so the call still fires and the
// backend can 401 informatively rather than failing silently client-side).
//
// Why this exists despite the global interceptor in src/utils/auth_interceptor.js:
// after a fresh mechanic-login → /mechanic-dashboard redirect, three axios
// calls in componentDidMount fired before the interceptor reliably attached
// the header (observed 401s on /auto/get_mechanic_status,
// /auto/check_mechanic_worker, /auto/get_mechanic_month_stats). Passing the
// header explicitly guarantees the request leaves the SPA with the JWT
// regardless of interceptor timing. The interceptor's STRIP-THEN-SET path
// will see the explicit header, dedupe it, and forward exactly one
// Authorization value — no duplicates.
const authHeaders = () => {
  const token = getLocalItem('authToken');
  return token ? { Authorization: 'Bearer ' + token } : {};
};

class MechanicDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedDate: moment.utc().tz('Asia/Kolkata').startOf('day').format('YYYY-MM-DD'),
      mechanic_phone: null,
      walletBalance: '',
      bookingsData: [],
      mechanic_name: '',
      selectedBookingId: null,
      bookingDetails: {},
      selectedBooking: null,
      modalIsOpen: false,
      wizardConfig: null,      // IMPL_06: pilot flag + language + tier
      showLangModal: false,    // IMPL_06: language confirm on first load
      showProfileBasics: false, // one-time language + T-shirt size form (all mechanics)
      selectedPart: '',
      selectedPartsList: [],
      commonParts: '',
      allParts: '',
      invoiceMessage: '',
      completedMessage: '',
      orderStatusMessage: '',
      locationUpdateInterval: null,
      mechanicStatus: '',
      invoiceGenerated: false,
      mechanicInventory: null,
      disableInvoiceButton: false,
      selectedBookingNotes: '',
      notesSuccessMessage: '',
      selectedPhotos: [],
      uploadSuccessMessage: '',
      bookingConfirmations: {},
      showConfirmationModal: false,
      isRequesting: false,
      requestStatus: '',
      submitNotesDisabled: false,
      selectedWorker: '',
      workerDetails: [],
      isWorker: false,
      totalBookingsCount: 0,
      nearbyVendors: [],
      selectedVendor: null,
      isBookingPorter: false,
      porterBookingMessage: '',
      isSendingOtp: false,
      isOtpSent: false,
      enteredOtp: '',
      otpVerificationMessage: '',
      isOtpVerified: false,
      monthlyStats: null,
      vehicleMakesCar: [],
      vehicleMakesBike: [],
      vehicleModels: {},    // { [makeValue]: [ {value,label}, ... ] }
      vehicleYears: [],
      masterDataFetched: false,
      settlementData: null,       // grouped: { today, previous, pending_cost_prices, totals }
      settlementLoading: false,
      settlingBookingId: null,    // Which booking is currently being settled
      previousExpanded: false,    // Whether "Previous Unsettled" section is expanded
      qrData: null,               // { qr_id, image_url, amount, booking_id } when QR is shown
      qrPolling: null,            // polling interval ID
      enforcementState: null,     // training enforcement (mandatory video); null => nothing shows
      enforcementLang: null,      // mechanic's chosen video language, if set
    };

    this.handleNotesChange = this.handleNotesChange.bind(this);
    this.submitNotesUpdate = this.submitNotesUpdate.bind(this);
    this.fetchBookingsData = this.fetchBookingsData.bind(this);
    this.callVendor = this.callVendor.bind(this);
    this.bookPorter = this.bookPorter.bind(this);
    this.handleVehicleMakeChange = this.handleVehicleMakeChange.bind(this);
    this.handleVehicleModelChange = this.handleVehicleModelChange.bind(this);
    this.handleVehicleYearChange = this.handleVehicleYearChange.bind(this);
    this.prefetchModelsForExistingBookings = this.prefetchModelsForExistingBookings.bind(this);
    this.fetchVehicleMasterData = this.fetchVehicleMasterData.bind(this);

    // Auto-refresh intervals
    this.bookingsRefreshInterval = null;
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  // IMPL_06 (2026-07-20): wizard/pilot config + language confirm + tier badge.
  fetchWizardConfig = async (mechanicPhone) => {
    try {
      const r = await axios.get('https://ridenrepair.com/api/mechanic_wizard_config',
        { params: { mechanic_phone: mechanicPhone }, headers: authHeaders() });
      const cfg = r.data || {};
      // Profile basics (language + T-shirt size) is ONE form for all mechanics and
      // takes over from the language modal, so nobody sees two forms back to back.
      // The language modal stays only as the fallback for a backend that does not
      // report profile_basics_complete yet. A failed fetch (catch below) shows nothing.
      const hasBasics = typeof cfg.profile_basics_complete === 'boolean';
      this.setState({
        wizardConfig: cfg,
        showProfileBasics: hasBasics && !cfg.profile_basics_complete,
        showLangModal: !hasBasics && !cfg.language_confirmed,
      });
    } catch (e) {
      // Non-blocking: dashboard works without wizard config.
      console.log('wizard config fetch failed', e.message);
    }
  };

  onProfileBasicsSaved = ({ language, tshirt_size }) => {
    this.setState(prev => ({
      showProfileBasics: false,
      wizardConfig: {
        ...(prev.wizardConfig || {}),
        language, language_confirmed: true, tshirt_size, profile_basics_complete: true,
      },
    }));
  };

  saveLanguage = async (lang) => {
    try {
      await axios.post('https://ridenrepair.com/api/v2/mechanic/language',
        { mechanic_phone: this.state.mechanic_phone, language: lang },
        { headers: authHeaders() });
      this.setState(prev => ({
        showLangModal: false,
        wizardConfig: { ...(prev.wizardConfig || {}), language: lang, language_confirmed: true },
      }));
    } catch (e) {
      this.setState({ showLangModal: false });
    }
  };

  componentDidMount() {
    this.props.setShowChatBot(false);
    const { mechanicName, mechanicPhone } = this.props.location.state;
    const dataToSend = { mechanic_phone: mechanicPhone };
    if (typeof window !== 'undefined') {
      window.postMessage(JSON.stringify(dataToSend));
    }

    this.fetchMechanicMonthStats(mechanicPhone);
    this.fetchWizardConfig(mechanicPhone);

    this.setState(
      {
        mechanic_name: mechanicName,
        mechanic_phone: mechanicPhone,
      },
      async () => {
        this.fetchBookingsData();
        this.fetchPartsData();
        this.checkMechanicWorker();
        const response = await axios.get("https://ridenrepair.com/auto/get_mechanic_status", { params: { mechanic_phone: mechanicPhone }, headers: authHeaders() });
        // Training enforcement: show the mandatory video modal only if the
        // backend has an active enforcement for this mechanic (empty otherwise).
        try {
          const es = response.data && response.data.enforcement_state;
          if (es && es.skill) {
            this.setState({
              enforcementState: es,
              enforcementLang: (response.data && response.data.preferred_language) || null,
            });
          }
        } catch (e) {}
        try {
          const inventoryReponse = await axios.get("https://ridenrepair.com/api/get_mechanic_inventory", { params: { mechanic_phone: mechanicPhone }, headers: authHeaders() });

          if (inventoryReponse.data && Object.keys(inventoryReponse.data.inventory).length > 0) {
            // keep only items where quantity > 0
            const inventory = Object.entries(inventoryReponse.data.inventory)
              .filter(([, { quantity }]) => quantity > 0)
              .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});

            this.setState({ mechanicInventory: inventory });
          }
        } catch (error) {
        }
        const get_balance = await axios.get("https://ridenrepair.com/auto/mechanic_balance", { params: { mechanic_phone: mechanicPhone }, headers: authHeaders() });
        this.setState({ mechanicStatus: response.data.status, walletBalance: get_balance.data.balance });

        const get_workers = await axios.get(`https://ridenrepair.com/auto/garage_workers?mechanic_phone=${mechanicPhone}`, { headers: authHeaders() });

        this.fetchVehicleMasterData();
        this.setState({ workerDetails: get_workers.data.workers });

        // Fetch settlement data (grouped: today vs previous)
        try {
            const settlementRes = await axios.get('https://ridenrepair.com/api/mechanic_settlement_bookings', {
                params: { mechanic_phone: mechanicPhone, grouped: true },
                headers: authHeaders()
            });
            this.setSettlementData(settlementRes.data);
        } catch (error) {
            console.log('Error fetching settlement data:', error);
        }
      }
    );

    // Set up auto-refresh every 5 minutes (300000 ms)
    this.bookingsRefreshInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.fetchBookingsData();
      }
    }, 300000); // 5 minutes

    // Listen for visibility changes (app coming to foreground)
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  componentWillUnmount() {
    // Clean up intervals
    if (this.bookingsRefreshInterval) {
      clearInterval(this.bookingsRefreshInterval);
    }
    // Remove visibility change listener
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handleVisibilityChange = () => {
    // When app comes to foreground, refresh bookings
    if (document.visibilityState === 'visible') {
      this.fetchBookingsData();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.selectedDate !== prevState.selectedDate) {
      this.fetchBookingsData();
    }
  }

  // Fetch vehicle makes (car & bike) and years. Once only.
  async fetchVehicleMasterData() {
    try {
      // Fetch car makes
      const makesCarRes = await axios.get('https://ridenrepair.com/auto/vehicle_makes', {
        params: { type: 'car' },
        headers: authHeaders()
      });
      // Fetch bike makes
      const makesBikeRes = await axios.get('https://ridenrepair.com/auto/vehicle_makes', {
        params: { type: 'bike' },
        headers: authHeaders()
      });
      // Fetch years
      const yearsRes = await axios.get('https://ridenrepair.com/auto/vehicle_years', { headers: authHeaders() });
      const makesCar = Array.isArray(makesCarRes.data.makes) ? makesCarRes.data.makes : [];
      const makesBike = Array.isArray(makesBikeRes.data.makes) ? makesBikeRes.data.makes : [];
      const years = Array.isArray(yearsRes.data.years) ? yearsRes.data.years : [];
      this.setState({
        vehicleMakesCar: makesCar,
        vehicleMakesBike: makesBike,
        vehicleYears: years,
        masterDataFetched: true
      }, () => {
        // After master data is in state, prefetch models for existing bookings if any
        this.prefetchModelsForExistingBookings();
      });
    } catch (err) {
      console.error('Error fetching vehicle master data', err);
    }
  }

  prefetchModelsForExistingBookings() {
    const { bookingsData, vehicleModels, masterDataFetched } = this.state;
    if (!masterDataFetched || !Array.isArray(bookingsData) || bookingsData.length === 0) return;
    // Collect unique makes from bookingsData
    const makesSet = new Set();
    bookingsData.forEach(booking => {
      const mk = booking.vehicle_make;
      if (mk) makesSet.add(mk);
    });
    makesSet.forEach(make => {
      if (!vehicleModels[make]) {
        // Find a booking that uses this make, to inspect its category
        const exampleBooking = bookingsData.find(b => b.vehicle_make === make);
        // determine type: default 'car' if no category field or exampleBooking not found
        let type = 'car';
        if (exampleBooking && exampleBooking.vehicle_category && typeof exampleBooking.vehicle_category === 'string') {
          type = exampleBooking.vehicle_category.toLowerCase() === 'bike' ? 'bike' : 'car';
        }
        // Fetch models for this make
        axios.get('https://ridenrepair.com/auto/vehicle_models', {
          params: { type, make: make.toLowerCase() },
          headers: authHeaders()
        })
        .then(res => {
          const modelsList = Array.isArray(res.data.models) ? res.data.models : [];
          this.setState(prev => ({
            vehicleModels: {
              ...prev.vehicleModels,
              [make]: modelsList
            }
          }));
        })
        .catch(err => {
          console.error('Error prefetching models for make', make, err);
        });
      }
    });
  }

  checkMechanicWorker = async () => {
    const response = await axios.post('https://www.ridenrepair.com/auto/check_mechanic_worker', {
      phone: this.state.mechanic_phone
    }, { headers: authHeaders() });
    this.setState({ isWorker: response.data.message })
  }

  fetchPartsData() {
    fetch('https://ridenrepair.com/api/get_common_parts')
      .then((response) => response.json())
      .then((data) => {
        this.setState({ commonParts: data });
      })
      .catch((error) => {
      });
    
    fetch('https://ridenrepair.com/api/get_all_parts')
      .then((response) => response.json())
      .then((data) => {
        this.setState({ allParts: data });
      })
      .catch((error) => {
      });
  }

  fetchBookingsData() {
    const { selectedDate, mechanic_phone } = this.state;

    fetch(`https://ridenrepair.com/api/get_booking_via_mechanic?date=${selectedDate}&mechanic_phone=${mechanic_phone}`)
      .then((response) => response.json())
      .then((data) => {
        // Define the sorting order for order_status
        const orderStatusPriority = ['COMPLETED', 'CASH TAKEN', 'SETTLED', 'DUPLICATE', ...CANCELLED_STATUSES];

        // Sort the bookings data based on order_status
        const sortedData = data.sort((a, b) => {
          const statusA = orderStatusPriority.indexOf(a.order_status);
          const statusB = orderStatusPriority.indexOf(b.order_status);

          // Place the known statuses (COMPLETED, CASH TAKEN, etc.) at the end in the specified order
          if (statusA !== -1 && statusB !== -1) {
            return statusA - statusB;
          }
          // If a.statusA is in the array and b.statusA is not, b comes first
          if (statusA !== -1) return 1;
          if (statusB !== -1) return -1;

          // For other statuses, keep the order unchanged
          return 0;
        });

        // Calculate total booking count
        const totalBookingsCount = sortedData.length;

        // Update the state with sorted data and booking count
        // this.setState({ bookingsData: sortedData, totalBookingsCount: totalBookingsCount });
        this.setState(
          { bookingsData: sortedData, totalBookingsCount: totalBookingsCount },
          () => {
            // If master data already fetched, prefetch model lists for existing bookings
            if (this.state.masterDataFetched) {
              this.prefetchModelsForExistingBookings();
            }
          }
        );
      })
      .catch((error) => {
      });
  }

    // Called when mechanic selects a vehicle make in the booking-details modal
  async handleVehicleMakeChange(event) {
    const make = event.target.value;
    // Update selectedBooking in state
    this.setState(prevState => {
      const selectedBooking = { ...(prevState.selectedBooking || {}) };
      selectedBooking.vehicle_make = make;
      // Clear model if make changed
      selectedBooking.vehicle_model = '';
      return { selectedBooking };
    }, async () => {
      const { selectedBooking, bookingsData } = this.state;
      // Persist to backend
      try {
        await axios.post('https://ridenrepair.com/api/update_booking_vehicle_make', {
          booking_id: selectedBooking.booking_id,
          email: selectedBooking.email,
          vehicle_make: make
        }, { headers: authHeaders() });
      } catch (err) {
        console.error('Error updating vehicle make for booking', err);
      }
      // Update bookingsData array so list shows updated make if displayed elsewhere
      const updatedBookingsData = bookingsData.map(b => {
        if (b.booking_id === selectedBooking.booking_id) {
          return { ...b, vehicle_make: make, vehicle_model: '' };
        }
        return b;
      });
      this.setState({ bookingsData: updatedBookingsData });

      // Fetch models for this make if not already in cache
      if (make) {
        const { vehicleModels } = this.state;
        if (!vehicleModels[make]) {
          // determine type; default to 'car' if not present
          let type = 'car';
          if (selectedBooking.vehicle_category && typeof selectedBooking.vehicle_category === 'string') {
            type = selectedBooking.vehicle_category.toLowerCase() === 'bike' ? 'bike' : 'car';
          }
          try {
            const res = await axios.get('https://ridenrepair.com/auto/vehicle_models', {
              params: { type, make: make.toLowerCase() },
              headers: authHeaders()
            });
            const modelsList = Array.isArray(res.data.models) ? res.data.models : [];
            this.setState(prev => ({
              vehicleModels: {
                ...prev.vehicleModels,
                [make]: modelsList
              }
            }));
          } catch (err) {
            console.error('Error fetching models for make', make, err);
          }
        }
      }
    });
  }

  // Called when mechanic selects a vehicle model
  async handleVehicleModelChange(event) {
    const model = event.target.value;
    this.setState(prevState => {
      const selectedBooking = { ...(prevState.selectedBooking || {}) };
      selectedBooking.vehicle_model = model;
      return { selectedBooking };
    }, async () => {
      const { selectedBooking, bookingsData } = this.state;
      try {
        await axios.post('https://ridenrepair.com/api/update_booking_vehicle_model', {
          booking_id: selectedBooking.booking_id,
          email: selectedBooking.email,
          vehicle_model: model
        }, { headers: authHeaders() });
      } catch (err) {
        console.error('Error updating vehicle model for booking', err);
      }
      // Update bookingsData
      const updatedBookingsData = bookingsData.map(b => {
        if (b.booking_id === selectedBooking.booking_id) {
          return { ...b, vehicle_model: model };
        }
        return b;
      });
      this.setState({ bookingsData: updatedBookingsData });
    });
  }

  // Called when mechanic selects a vehicle year
  async handleVehicleYearChange(event) {
    const year = event.target.value;
    this.setState(prevState => {
      const selectedBooking = { ...(prevState.selectedBooking || {}) };
      selectedBooking.vehicle_year = year;
      return { selectedBooking };
    }, async () => {
      const { selectedBooking, bookingsData } = this.state;
      try {
        await axios.post('https://ridenrepair.com/api/update_booking_vehicle_year', {
          booking_id: selectedBooking.booking_id,
          email: selectedBooking.email,
          vehicle_year: year
        }, { headers: authHeaders() });
      } catch (err) {
        console.error('Error updating vehicle year for booking', err);
      }
      // Update bookingsData
      const updatedBookingsData = bookingsData.map(b => {
        if (b.booking_id === selectedBooking.booking_id) {
          return { ...b, vehicle_year: year };
        }
        return b;
      });
      this.setState({ bookingsData: updatedBookingsData });
    });
  }

  handleChangeDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    this.setState({ selectedDate: formattedDate });
  };

  handleBookingClick = (bookingId) => {
    // Navigate to booking details page
    this.props.history.push(`/mechanic-dashboard/${bookingId}`);
  };

  sendOtpToCustomer = async () => {
    // Immediately disable the button to prevent repeated clicks
    this.setState({ isSendingOtp: true, otpVerificationMessage: '' });
  
    try {
      const { selectedBooking } = this.state;
      
      // Validate required fields
      if (!selectedBooking || !selectedBooking.phone || !selectedBooking.booking_source) {
        this.setState({
          otpVerificationMessage: 'Missing booking information. Please try again.',
          isSendingOtp: false
        });
        return;
      }

      const response = await axios.post('https://www.ridenrepair.com/api/send_start_service_otp_sms', {
        phone: selectedBooking.phone,
        booking_source: selectedBooking.booking_source
      }, { headers: authHeaders() });

      if (response.status === 200) {
        this.setState({
          isOtpSent: true,
          otpVerificationMessage: 'OTP sent to customer successfully.',
        });
      } else {
        this.setState({
          otpVerificationMessage: 'Failed to send OTP. Please try again.',
          isSendingOtp: false
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Error sending OTP. Please try again.';
      this.setState({
        otpVerificationMessage: `Error: ${errorMessage}`,
        isSendingOtp: false
      });
    }
  };
  
  // Send OTP to customer
  // sendOtpToCustomer = async () => {
  //   this.setState({ isSendingOtp: true });
  //   const { selectedBooking } = this.state;
  //   try {
  //     const response = await axios.post('https://www.ridenrepair.com/api/send_start_service_otp_sms', {
  //       booking_id: selectedBooking.booking_id,
  //       phone: selectedBooking.phone,
  //       booking_source: selectedBooking.booking_source
  //     });

  //     if (response.status === 200) {
  //       this.setState({ isOtpSent: true, otpVerificationMessage: 'OTP sent to customer.' });
  //     } else {
  //       this.setState({ otpVerificationMessage: 'Failed to send OTP. Please try again.' });
  //     }
  //   } catch (error) {
  //     this.setState({ otpVerificationMessage: 'Error sending OTP. Please try again.' });
  //   }
  // };

  // Verify OTP entered by the mechanic. Uses the dedicated mechanic-only
  // endpoint, which validates OTP, advances booking to SERVICE STARTED, and
  // fires the service_started Temporal event tagged actor_type='mechanic'.
  // No client-side updateBookingStatus chain — the server does both writes.
  verifyOtp = async () => {
    const { enteredOtp, selectedBooking, mechanic_phone, bookingsData } = this.state;
    try {
      const response = await axios.post('https://www.ridenrepair.com/api/validate_service_start_otp', {
        booking_id: selectedBooking.booking_id,
        email: selectedBooking.email,
        phone: selectedBooking.phone,
        booking_otp: enteredOtp,
        mechanic_phone: mechanic_phone,
      }, { headers: authHeaders() });

      if (response.status === 200 && response.data.is_valid) {
        this.setState({ isOtpVerified: true, otpVerificationMessage: 'OTP verified successfully.' });
        // Reflect the new status locally — server already persisted it.
        const updatedBookingsData = bookingsData.map(b =>
          b.booking_id === selectedBooking.booking_id
            ? { ...b, order_status: 'SERVICE STARTED' }
            : b
        );
        this.setState({ bookingsData: updatedBookingsData });
      } else {
        this.setState({ otpVerificationMessage: 'Invalid OTP. Please try again.' });
      }
    } catch (error) {
      this.setState({ otpVerificationMessage: 'Error verifying OTP. Please try again.' });
    }
  };

  callVendor = () => {
    try {
      const { selectedVendor, mechanic_phone } = this.state;
      const params = {
        mechanic_number: mechanic_phone,
        phone: selectedVendor.phone,
        booking_id: this.state.selectedBooking.booking_id,
      };
      axios.post('https://ridenrepair.com/api/sip_connect_mechanic_to_vendor', params, { headers: authHeaders() });
    } catch (error) {
    }
  };

  bookPorter = async () => {
    this.setState({ isBookingPorter: true, porterBookingMessage: 'Booking Porter...' });
    try {
      const { selectedVendor, mechanic_phone, selectedBooking } = this.state;
      const params = {
        vendor_phone: selectedVendor.phone,
        mechanic_phone: mechanic_phone,
        booking_id: selectedBooking.booking_id
      };
      const response = await axios.post('https://ridenrepair.com/api/create_pickup_order', params, { headers: authHeaders() });
      if (response.status === 201) {
        this.setState({ porterBookingMessage: 'Porter booked successfully!' });
      } else {
        this.setState({ porterBookingMessage: 'Failed to book porter.' });
      }
    } catch (error) {
      this.setState({ porterBookingMessage: 'Error booking porter.' });
    } finally {
      // Disable the button for 5 seconds
      setTimeout(() => {
        this.setState({ isBookingPorter: false, porterBookingMessage: '' });
      }, 5000);
    }
  };

  closeModal = () => {
    const { selectedBooking, bookingConfirmations } = this.state;

    const validStatuses = [...IN_PROGRESS_STATUSES, ...TERMINAL_STATUSES]
    
    if (validStatuses.includes(selectedBooking.order_status)) {
      this.setState({ modalIsOpen: false, selectedBooking: null, selectedPhotos: [], selectedBookingNotes: '' });
    } else if (bookingConfirmations[selectedBooking.booking_id] === undefined) {
      this.setState({ showConfirmationModal: true });
    } else {
      this.setState({ modalIsOpen: false, selectedBooking: null, selectedPhotos: [], selectedBookingNotes: '' });
    }

    this.setState({ 
      selectedPartsList: [],
      selectedVendor: null, 
      nearbyVendors: [], 
      porterBookingMessage: '' 
    });
  };

  handleLogout = () => {
    deleteLocalItem('authToken');
    const { history } = this.props;
    history.push('/mechanic-login');
  };

  formatDate = (dateString) => {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const year = date.getUTCFullYear();
    const month = months[date.getUTCMonth()];
    const day = date.getUTCDate();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = (hours % 12 || 12).toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');

    return `${month} ${day}, ${year} ${formattedHours}:${formattedMinutes} ${ampm}`;
  }

  handleClickPart = (part) => {
    const { selectedPartsList } = this.state;
    const newPart = {
      partId: part.part_id, 
      partName: part.part_name, 
      salesPrice: '', 
      costPrice: ''
    }
    this.setState({ 
      selectedPart: newPart,
      selectedPartsList: [...selectedPartsList, newPart]
    })
  };

  handleAddOtherPart = () => {
    const { selectedPartsList } = this.state;
    const newPart = {
      partName: '', 
      salesPrice: '', 
      costPrice: ''
    }
    this.setState({ 
      selectedPart: newPart,
      selectedPartsList: [...selectedPartsList, newPart]
    })
  };

  handleOtherPartNameChange = (index, value) => {
    const { selectedPartsList } = this.state;
    const updatedSelectedPartsList = [...selectedPartsList];
    updatedSelectedPartsList[index].partName = value;
    this.setState({
      selectedPartsList: updatedSelectedPartsList
    });
  };

  handleSelectPart = (event) => {
    event.preventDefault();
    const selectedPartId = event.target.options[event.target.selectedIndex].getAttribute('data-part-id');
    const selectedPartName = event.target.options[event.target.selectedIndex].getAttribute('data-part-name');
    const { selectedPartsList } = this.state;
    const newPart = {
      partId: selectedPartId, 
      partName: selectedPartName,
      salesPrice: '', 
      costPrice: ''
    }
    this.setState({ 
      selectedPart: newPart,
      selectedPartsList: [...selectedPartsList, newPart]
    })
  };

  handleNotesChange(event) {
    this.setState({ selectedBookingNotes: event.target.value });
  }

  submitNotesUpdate() {
    const { selectedBookingNotes, selectedBooking } = this.state;
    const { booking_id, email } = selectedBooking;

    this.setState({ submitNotesDisabled: true });

    axios.post('https://ridenrepair.com/api/update_booking_mechanic_notes', {
      booking_id,
      email,
      mechanic_notes: encodeURIComponent(selectedBookingNotes) + ' by ' + this.state.mechanic_name
    }, { headers: authHeaders() })
    .then(response => {
      // Handle success
      this.setState({ notesSuccessMessage: 'Notes updated successfully!' });
    })
    .catch(error => {
      // Handle error
      this.setState({ notesSuccessMessage: 'Failed to update notes. Please try again.' });
    })
    .finally(() => {
      // Enable the button after 3 seconds
      setTimeout(() => {
        this.setState({ submitNotesDisabled: false });
      }, 3000);
    });
  }

  handleQuantityChange = (index, quantity) => {
    const { selectedPartsList } = this.state;
    const updatedSelectedPartsList = [...selectedPartsList];
    updatedSelectedPartsList[index].quantity = quantity;
    this.setState({ selectedPartsList: updatedSelectedPartsList });
  };
  
  removePart = (index) => {
    const { selectedPartsList } = this.state;
    const updatedSelectedPartsList = [...selectedPartsList];
    updatedSelectedPartsList.splice(index, 1);
    this.setState({ selectedPartsList: updatedSelectedPartsList });
  };

  handleSalesPriceChange = (index, salesPrice) => {
    const { selectedPartsList } = this.state;
    const updatedSelectedPartsList = [...selectedPartsList];
    updatedSelectedPartsList[index].salesPrice = salesPrice;
    this.setState({ selectedPartsList: updatedSelectedPartsList });
  };

  requestNewBooking = () => {
    const params = {
      mechanic_phone: this.state.mechanic_phone
    }
    
    axios.post('https://ridenrepair.com/auto/booking_request_by_mechanic', params, { headers: authHeaders() })
      .then(response => {
        // Check if booking_id is present in the response data
        if (response.data.booking_id) {
          this.setState({ requestStatus: 'New booking successfully requested!' });
          // Fetch updated bookings data or update UI as needed
          this.fetchBookingsData();
        } else {
          this.setState({ requestStatus: 'No booking found.' });
        }
      })
      .catch(error => {
        this.setState({ requestStatus: 'Error: Unable to request booking.' });
      })
      .finally(() => {
        this.setState({ isRequesting: false });
        // Clear requestStatus after 20 seconds
        setTimeout(() => {
          this.setState({ requestStatus: '' });
        }, 20000); // 20 seconds in milliseconds
      });
  }

  fetchMechanicMonthStats = async (mechanicPhone) => {
    try {
      const response = await axios.get('https://www.ridenrepair.com/auto/get_mechanic_month_stats', {
        params: {
          mechanic_phone: mechanicPhone
        },
        headers: authHeaders()
      });
      if (response.data.status === 'success') {
        this.setState({ monthlyStats: response.data.data });
      } else {
      }
    } catch (error) {
    }
  }
  
  // handleCostPriceChange = (index, costPrice) => {
  //   const { selectedPartsList } = this.state;
  //   const updatedSelectedPartsList = [...selectedPartsList];
  //   updatedSelectedPartsList[index].costPrice = costPrice;
  //   this.setState({ selectedPartsList: updatedSelectedPartsList });
  // };

  callCustomer = () => {
    try {
      this.setState({ invoiceMessage: '', completedMessage: '', orderStatusMessage: '' });
      const { selectedBooking, mechanic_phone } = this.state;
      const params = {
        mechanic_number: mechanic_phone,
        phone: selectedBooking.phone,
        booking_id: selectedBooking.booking_id
      }
      axios.post(`https://ridenrepair.com/api/sip_connect_mechanic_to_customer`, params, { headers: authHeaders() });
    } catch (error) {
    }
  }

  updateBookingStatus = (order_status) => {
    const { selectedBooking, bookingsData, mechanic_phone } = this.state;
    // actor_type/actor_id let server.js tag the resulting Temporal event as
    // mechanic-driven (service_in_progress / service_started / etc).
    // Without these, server.js defaults to actor_type='ops' and the event
    // becomes indistinguishable from an ops dashboard click.
    const params = {
      booking_id: selectedBooking.booking_id,
      email: selectedBooking.email,
      order_status: order_status,
      change_history: 'Status updated by ' + this.state.mechanic_name,
      actor_type: 'mechanic',
      actor_id: mechanic_phone,
      mechanic_phone: mechanic_phone,
    }

    // Update the order status in the bookingsData state
    const updatedBookingsData = bookingsData.map(booking => {
      if (booking.booking_id === selectedBooking.booking_id) {
        return {
          ...booking,
          order_status: order_status
        };
      }
      return booking;
    });

    // Update the state with the modified bookingsData
    this.setState({ bookingsData: updatedBookingsData });

    if (order_status === 'COMPLETED' || order_status === 'CASH TAKEN') {
      const smsCustFeedbackParams = {
        phone: selectedBooking.phone
      }

      // try {
      //   axios.post("https://ridenrepair.com/auto/reduce_mechanic_daily_count", { mechanic_phone: selectedBooking['mechanic_number'] });
      // } catch (error) {
      // }
      try {
          axios.post('https://ridenrepair.com/api/send_customer_feedback_sms', smsCustFeedbackParams, { headers: authHeaders() });
      } catch (error) {
      }
      this.setState({ completedMessage: 'Booking Completed' })
      this.setState({ orderStatusMessage: ''})
    } else {
      this.setState({ completedMessage: '' })
      this.setState({ orderStatusMessage: order_status })
    }
    try {
      axios.post(`https://ridenrepair.com/api/update_booking_status`, params, { headers: authHeaders() });
    } catch (error) {
    }
    try {
      axios.post(`https://ridenrepair.com/api/update_booking_change_history`, params, { headers: authHeaders() });
    } catch (error) {
    }
  }

  updateMechanicStatus = (status) => {
    this.setState({ mechanicStatus: status })
    const params = {
      phone: this.state.mechanic_phone,
      status: status
    }
    try {
      axios.post('https://ridenrepair.com/auto/mechanic_status', params, { headers: authHeaders() })
    } catch (error) {
    }
  }

  generateInvoice = async () => {
    this.setState({ invoiceMessage: 'Generating Invoice', disableInvoiceButton: true });
    if (!this.state.invoiceGenerated || this.state.disableInvoiceButton) {
      try {
        const { selectedBooking, selectedPartsList } = this.state;
        if (selectedPartsList.some((part) => !part.salesPrice)) {
          this.setState({ invoiceMessage: 'Please fill in all the Sales prices' });
          return; // Exit the function if salesPrice is missing for any part
        }
        const partsNames = selectedPartsList.map((part) => part.partName);
        const salesPrices = selectedPartsList.map((part) => part.salesPrice);

        let email = '';
        if (selectedBooking.secondary_email) {
          email = selectedBooking.secondary_email;
        } else {
          email = selectedBooking.email;
        }
        const params = {
          booking_id: selectedBooking.booking_id,
          phone: selectedBooking.phone,
          email: email,
          charges_desc: partsNames.join(','),
          amount_details: salesPrices.join(','),
          paid_amount: selectedBooking.paid_amount,
        };
        
        const paymentLink = await axios.get("https://ridenrepair.com/api/get_rzp_pay_link", { params, headers: authHeaders() });

        selectedBooking['payment_link'] = paymentLink.data.short_url;
        selectedBooking['payment_link_id'] = paymentLink.data.payment_link_id;
        selectedBooking['final_payment_amount'] = paymentLink.data.final_amount;
        selectedBooking['invoice_id'] = paymentLink.data.invoice_id;
        selectedBooking['charges_desc'] = params.charges_desc;
        selectedBooking['amount_details'] = params.amount_details;
        
        if (paymentLink.data.prepaid) {
          const mechanicparams = {
            phone: selectedBooking['mechanic_number'],
            status: 'AVAILABLE',
          }
          try {
              axios.post("https://ridenrepair.com/auto/mechanic_status", mechanicparams, { headers: authHeaders() });
          } catch (error) {
          }
        } else {
          try {
              axios.post(`https://ridenrepair.com/api/update_booking_invoice`, selectedBooking, { headers: authHeaders() });
          } catch (error) {
          }
          try {
              axios.post("https://ridenrepair.com/whatsapp/sendpaylink", selectedBooking, { headers: authHeaders() });
          } catch (error) {
          }
          const mechanic_params = {
              phone: selectedBooking['mechanic_number'],
              status: 'AVAILABLE',
          }
          try {
              axios.post("https://ridenrepair.com/auto/mechanic_status", mechanic_params, { headers: authHeaders() });
          } catch (error) {
          }
        }
        this.setState({ selectedBooking });
        this.setState({ invoiceMessage: 'Invoice Generated' });
        this.setState({ invoiceGenerated: true });

        // Set a timeout to re-enable the button after 5 seconds
        setTimeout(() => {
          this.setState({ disableInvoiceButton: false });
        }, 5000);
      } catch (error) {
      }
    }
  }

  // Regenerate the customer's Razorpay payment link for an EXISTING invoice
  // (invoices are created via WhatsApp/ops, not the app). Use when the
  // customer's link expired or the payment failed: re-mints a fresh link for
  // the same charges and resends it to the customer.
  regeneratePaymentLink = async () => {
    const { selectedBooking } = this.state;
    if (!selectedBooking || !selectedBooking.charges_desc || !selectedBooking.amount_details) {
      this.setState({ invoiceMessage: 'No invoice on this booking yet — please get one generated first.' });
      return;
    }
    this.setState({ invoiceMessage: 'Regenerating payment link…', disableInvoiceButton: true });
    try {
      const email = selectedBooking.secondary_email || selectedBooking.email;
      const params = {
        booking_id: selectedBooking.booking_id,
        phone: selectedBooking.phone,
        email,
        charges_desc: selectedBooking.charges_desc,
        amount_details: selectedBooking.amount_details,
        paid_amount: selectedBooking.paid_amount,
      };
      const res = await axios.get('https://ridenrepair.com/api/get_rzp_pay_link', { params, headers: authHeaders() });
      selectedBooking['payment_link'] = res.data.short_url;
      selectedBooking['payment_link_id'] = res.data.payment_link_id;
      selectedBooking['final_payment_amount'] = res.data.final_amount;
      selectedBooking['invoice_id'] = res.data.invoice_id;
      try { await axios.post('https://ridenrepair.com/api/update_booking_invoice', selectedBooking, { headers: authHeaders() }); } catch (e) {}
      try { await axios.post('https://ridenrepair.com/whatsapp/sendpaylink', selectedBooking, { headers: authHeaders() }); } catch (e) {}
      this.setState({ selectedBooking, invoiceMessage: 'New payment link sent to the customer ✅' });
    } catch (e) {
      this.setState({ invoiceMessage: 'Could not regenerate — please try again.' });
    } finally {
      setTimeout(() => this.setState({ disableInvoiceButton: false }), 5000);
    }
  }

  handlePhotoUpload = (event) => {
    const files = Array.from(event.target.files);
    this.setState((prevState) => ({
      selectedPhotos: [...prevState.selectedPhotos, ...files],
    }));
  };

  closeConfirmationModal = (isGoing) => {
    const { selectedBooking } = this.state;
    const updatedConfirmations = {
      ...this.state.bookingConfirmations,
      [selectedBooking.booking_id]: isGoing,
    };
    
    this.setState({
      bookingConfirmations: updatedConfirmations,
      showConfirmationModal: false,
    }, () => this.closeModal());
  };

  // WebView "Yes, I'm Going" path. Posts directly to the mechanic-tagged
  // accept endpoint instead of the older update_booking_status('MECHANIC
  // CONFIRMED') route — that older path is in SKIP_TEMPORAL_IN_STATUS_UPDATE
  // and silently dropped no event, leaving WebView-driven accepts invisible
  // to the attribution funnel. The new path fires a mechanic_accepted
  // Temporal event with actor_type='mechanic' and is idempotent on the
  // server side.
  acceptBookingFromWebView = () => {
    const { selectedBooking, mechanic_phone, bookingsData } = this.state;
    const params = {
      booking_id: selectedBooking.booking_id,
      mechanic_phone: mechanic_phone,
      // No FCM notification_id here (this is a webview tap, not a push).
      // Server falls back to a {booking_id, mechanic_phone} 60s dedup
      // window — fine for human taps on a button.
    };
    axios.post('https://ridenrepair.com/api/booking_accepted_by_mechanic', params, { headers: authHeaders() })
      .catch(err => {
        console.log('booking_accepted_by_mechanic failed (non-blocking):', err.message);
      });

    // Optimistic local-state update: mark the booking as MECHANIC CONFIRMED
    // so the UI reflects acceptance immediately. Server-side, the
    // /api/booking_accepted_by_mechanic handler will eventually reflect this
    // via downstream lock_booking + booking_event writes.
    const updatedBookingsData = bookingsData.map(booking =>
      booking.booking_id === selectedBooking.booking_id
        ? { ...booking, order_status: 'MECHANIC CONFIRMED' }
        : booking
    );
    this.setState({ bookingsData: updatedBookingsData });
  };

  handleBookingDenial = () => {
    const { selectedBooking, mechanic_name, mechanic_phone } = this.state;

    const params = {
      booking_id: selectedBooking.booking_id,
      email: selectedBooking.email,
      mechanic_phone: mechanic_phone,
      mechanic_name: mechanic_name,
      order_status: 'MECHANIC DENIED'
    };

    try {
      axios.post('https://ridenrepair.com/api/update_booking_status', params, { headers: authHeaders() });
    } catch (error) {
    }

    try {
      axios.post('https://ridenrepair.com/auto/mechanic_reallocate', params, { headers: authHeaders() });
    } catch (error) {
    }

    this.closeConfirmationModal(false);

  }
  
  uploadPhotos = async () => {
    const { selectedBooking, selectedPhotos } = this.state;

    // iOS app: WKWebView cannot perform multipart uploads, so route through the
    // native bridge. This flag is set ONLY by the iOS app — web and Android fall
    // through to the unchanged axios upload below.
    if (typeof window !== 'undefined' && window.__rnrIOSNativeUpload && window.ReactNativeWebView) {
      try {
        const toB64 = (file) => new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(String(r.result).split(',')[1] || '');
          r.onerror = () => rej(new Error('read failed'));
          r.readAsDataURL(file);
        });
        const photos = [];
        for (const f of selectedPhotos) {
          photos.push({ name: f.name || 'photo.jpg', type: f.type || 'image/jpeg', data: await toB64(f) });
        }
        window.__rnrBookingPhotoResult = (success, message) => {
          this.setState({ uploadSuccessMessage: message || (success ? 'Photos uploaded successfully!' : 'Failed to upload photos.') });
        };
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'NATIVE_BOOKING_PHOTO_UPLOAD',
          booking_id: selectedBooking.booking_id,
          email: selectedBooking.email,
          photos,
        }));
      } catch (e) {
        this.setState({ uploadSuccessMessage: 'Error uploading photos.' });
      }
      return;
    }

    const formData = new FormData();
    
    formData.append('booking_id', selectedBooking.booking_id);
    formData.append('email', selectedBooking.email);
    selectedPhotos.forEach((file) => {
      formData.append('photos', file);
    });

    try {
      const response = await axios.post('https://www.ridenrepair.com/api/upload_booking_photo', formData, { headers: authHeaders() });

      if (response.status === 200) {
        this.setState({ uploadSuccessMessage: 'Photos uploaded successfully!' });
      } else {
        this.setState({ uploadSuccessMessage: 'Failed to upload photos.' });
      }
    } catch (error) {
      this.setState({ uploadSuccessMessage: 'Error uploading photos.' });
    }
  };

  capitalizeFirstLetter = (input) => {
    return input.charAt(0).toUpperCase() + input.slice(1);
  }

  // ── Duplicate-payment guard ──────────────────────────────────────────
  // Root cause of double-pays (e.g. booking 143987 paid twice on 2026-05-30):
  // a mechanic pays a booking, but the backend settle lags (webhook/poll can
  // take minutes — once observed 5.5h). During that lag the booking still
  // shows as payable, so the mechanic — or a reopened app — pays it AGAIN.
  // Money is captured twice; one payment is wasted.
  //
  // Fix: the instant a payment succeeds we stamp the booking_id(s) into a
  // localStorage "settle-pending" set with a timestamp. The settlement list
  // hides anything in that set, so an already-paid booking can never be paid
  // again — even across app restarts — until the backend confirms it's
  // settled (at which point it naturally drops out of the unsettled list and
  // we prune it). Entries auto-expire after 24h as a safety valve.
  _PENDING_KEY = 'rnr_settle_pending_v1';
  _PENDING_TTL_MS = 24 * 60 * 60 * 1000;

  _readPendingSettlements = () => {
    try {
      const raw = localStorage.getItem(this._PENDING_KEY);
      const map = raw ? JSON.parse(raw) : {};
      const now = Date.now();
      let changed = false;
      for (const k of Object.keys(map)) {
        if (now - map[k] > this._PENDING_TTL_MS) { delete map[k]; changed = true; }
      }
      if (changed) localStorage.setItem(this._PENDING_KEY, JSON.stringify(map));
      return map;
    } catch (e) { return {}; }
  };

  // Mark booking_id(s) as paid-but-settle-pending right after a successful payment.
  recordPendingSettlements = (ids) => {
    try {
      const map = this._readPendingSettlements();
      const now = Date.now();
      (Array.isArray(ids) ? ids : [ids]).forEach(id => {
        if (id != null && id !== 'ALL') map[String(id)] = now;
      });
      localStorage.setItem(this._PENDING_KEY, JSON.stringify(map));
    } catch (e) {}
  };

  // Return settlementData with paid-pending bookings removed from the
  // unsettled lists (today + previous) and totals recomputed. Also prunes the
  // pending set of any id that the backend no longer reports as unsettled
  // (i.e. it has been settled) so the list self-heals.
  applyPendingFilter = (data) => {
    if (!data) return data;
    const pending = this._readPendingSettlements();
    const pendingIds = new Set(Object.keys(pending));
    if (pendingIds.size === 0) return data;

    const stillUnsettled = new Set();
    const filterList = (list) => (list || []).filter(b => {
      const idStr = String(b.booking_id);
      if (pendingIds.has(idStr)) { stillUnsettled.add(idStr); return false; }
      return true;
    });

    const out = JSON.parse(JSON.stringify(data));
    const sumAmt = (list) => (list || []).reduce((s, b) => s + (parseFloat(b.settlement_amount) || 0), 0);
    if (out.today) {
      out.today.unsettled_cash_bookings = filterList(out.today.unsettled_cash_bookings);
      out.today.total_cash_owed = sumAmt(out.today.unsettled_cash_bookings);
    }
    if (out.previous) {
      out.previous.unsettled_cash_bookings = filterList(out.previous.unsettled_cash_bookings);
      out.previous.total_cash_owed = sumAmt(out.previous.unsettled_cash_bookings);
    }
    if (out.totals) {
      const cashTotal = (out.today ? out.today.total_cash_owed : 0) + (out.previous ? out.previous.total_cash_owed : 0);
      out.totals.total_mechanic_owes_rnr = cashTotal;
      if (out.totals.net_balance != null) {
        out.totals.net_balance = (out.totals.total_rnr_owes_mechanic || 0) - cashTotal;
      }
    }

    // Self-heal: any pending id the backend NO LONGER lists as unsettled has
    // been settled — drop it from the pending set.
    let changed = false;
    for (const id of pendingIds) {
      if (!stillUnsettled.has(id)) { delete pending[id]; changed = true; }
    }
    if (changed) {
      try { localStorage.setItem(this._PENDING_KEY, JSON.stringify(pending)); } catch (e) {}
    }
    return out;
  };

  // Single entry point for setting settlement data so the pending-filter is
  // always applied — no settle list ever shows a booking that's been paid.
  setSettlementData = (data) => {
    this.setState({ settlementData: this.applyPendingFilter(data) });
  };

  onSuccessfulPayment = async (data, razorpayResponse) => {
    const mechanic_phone = this.state.mechanic_phone;

    // Settle all unsettled CASH TAKEN bookings via bulk endpoint
    try {
        const settlementData = this.state.settlementData;
        if (settlementData) {
            const todayBookings = (settlementData.today || {}).unsettled_cash_bookings || [];
            const prevBookings = (settlementData.previous || {}).unsettled_cash_bookings || [];
            const allBookings = [...todayBookings, ...prevBookings];
            if (allBookings.length > 0) {
                const booking_ids = allBookings.map(b => b.booking_id);
                this.recordPendingSettlements(booking_ids);
                const total_amount = allBookings.reduce((s, b) => s + (parseFloat(b.settlement_amount) || 0), 0);
                await axios.post('https://ridenrepair.com/api/settle_multiple_bookings_cash', {
                    mechanic_phone,
                    booking_ids,
                    razorpay_payment_id: razorpayResponse ? razorpayResponse.razorpay_payment_id : '',
                    total_amount
                }, { headers: authHeaders() });
            }
        }
    } catch (err) {
        console.log('Error settling bookings in bulk:', err);
    }

    // Refresh settlement data after payment
    try {
        const settlementRes = await axios.get('https://ridenrepair.com/api/mechanic_settlement_bookings', {
            params: { mechanic_phone, grouped: true },
            headers: authHeaders()
        });
        this.setSettlementData(settlementRes.data);
    } catch (err) {}
  }

  settleBooking = async (booking) => {
    this.setState({ settlingBookingId: booking.booking_id });
    try {
        // Create Razorpay order
        const amount = parseFloat(booking.settlement_amount) * 100; // paise
        const res = await this.loadScript("https://checkout.razorpay.com/v1/checkout.js");
        if (!res) {
            alert("Razorpay SDK failed to load");
            this.setState({ settlingBookingId: null });
            return;
        }

        const orderRes = await fetch('https://ridenrepair.com/api/razorpay', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                amount: amount,
                currency: 'INR',
                booking_id: booking.booking_id,
                email: booking.email,
                mechanic_phone: this.state.mechanic_phone,
                purpose: 'cash_settlement',
            })
        });
        let orderData = null;
        try { orderData = await orderRes.json(); } catch (e) {}
        // No order ⇒ never open checkout (an order-less checkout charges the raw amount as paise, e.g. Rs.1229 → Rs.12.29)
        if (!orderRes.ok || !orderData || !orderData.id) {
            alert(this.paymentRefusedText(orderRes.status));
            this.setState({ settlingBookingId: null });
            return;
        }

        const options = {
            key: 'rzp_live_v9Y9ZhDUWFgjNI',
            amount: orderData.amount,
            currency: 'INR',
            name: 'Ride N Repair',
            description: `Settlement for Booking #${booking.booking_id}`,
            order_id: orderData.id,
            handler: async (response) => {
                // Guard against double-pay: stamp this booking as paid-pending
                // immediately so it can't be paid again while settle lags.
                this.recordPendingSettlements([booking.booking_id]);
                // Mark booking as settled
                try {
                    await axios.post('https://ridenrepair.com/api/settle_booking_cash', {
                        booking_id: booking.booking_id,
                        email: booking.email,
                        mechanic_phone: this.state.mechanic_phone,
                        razorpay_payment_id: response.razorpay_payment_id
                    }, { headers: authHeaders() });
                    // Refresh settlement data
                    const settlementRes = await axios.get('https://ridenrepair.com/api/mechanic_settlement_bookings', {
                        params: { mechanic_phone: this.state.mechanic_phone, grouped: true },
                        headers: authHeaders()
                    });
                    this.setSettlementData(settlementRes.data);
                    this.setState({ settlingBookingId: null });
                } catch (err) {
                    console.log('Error settling booking:', err);
                    this.setState({ settlingBookingId: null });
                }
            },
            prefill: {
                contact: this.state.mechanic_phone
            },
            config: {
                display: {
                    blocks: {
                        upi: {
                            name: 'Pay via UPI',
                            instruments: [
                                { method: 'upi', flows: ['qr', 'collect', 'intent'] }
                            ]
                        }
                    },
                    sequence: ['block.upi'],
                    preferences: { show_default_blocks: true }
                }
            }
        };

        const paymentObject = new window.Razorpay(options);
        paymentObject.open();
    } catch (error) {
        console.log('Settlement error:', error);
        this.setState({ settlingBookingId: null });
    }
  }

  showSettlementQR = async (booking) => {
    try {
      const res = await axios.post('https://ridenrepair.com/api/create_settlement_qr', {
        booking_id: booking.booking_id,
        email: booking.email,
        mechanic_phone: this.state.mechanic_phone,
        amount: parseFloat(booking.settlement_amount)
      }, { headers: authHeaders() });
      if (res.data.success) {
        this.setState({ qrData: { ...res.data, booking_id: booking.booking_id, expires_at: Date.now() + 15 * 60 * 1000 } });
        // Poll for payment every 3 seconds
        const pollId = setInterval(async () => {
          try {
            const status = await axios.get('https://ridenrepair.com/api/settlement_qr_status', {
              params: { qr_id: res.data.qr_id },
              headers: authHeaders()
            });
            if (!status.data.paid && status.data.status === 'closed') {
              clearInterval(pollId);
              this.setState({ qrData: null, qrPolling: null });
              alert('This QR has expired and no payment was received. Please do not pay on it. Generate a new QR to pay your dues.');
              return;
            }
            if (status.data.paid) {
              clearInterval(pollId);
              this.recordPendingSettlements([booking.booking_id]);
              // Refresh settlement data
              const settlementRes = await axios.get('https://ridenrepair.com/api/mechanic_settlement_bookings', {
                params: { mechanic_phone: this.state.mechanic_phone, grouped: true },
                headers: authHeaders()
              });
              this.setState({ qrData: null, qrPolling: null });
              this.setSettlementData(settlementRes.data);
            }
          } catch (e) {}
        }, 3000);
        this.setState({ qrPolling: pollId });
      }
    } catch (error) {
      console.log('Error creating QR:', error);
      alert(error.response && error.response.status === 409 ? this.paymentRefusedText(409) : 'Failed to generate QR code');
    }
  }

  showSettleAllQR = async () => {
    const settlementData = this.state.settlementData;
    if (!settlementData) return;
    const todayBookings = (settlementData.today || {}).unsettled_cash_bookings || [];
    const prevBookings = (settlementData.previous || {}).unsettled_cash_bookings || [];
    const allBookings = [...todayBookings, ...prevBookings];
    if (allBookings.length === 0) return;

    const totalAmount = allBookings.reduce((s, b) => s + (parseFloat(b.settlement_amount) || 0), 0);
    const booking_ids = allBookings.map(b => b.booking_id);

    try {
      const res = await axios.post('https://ridenrepair.com/api/create_settlement_qr', {
        booking_ids: booking_ids,
        email: allBookings[0].email,
        mechanic_phone: this.state.mechanic_phone,
        amount: totalAmount,
        purpose: 'aggregate_cash_settlement',
      }, { headers: authHeaders() });
      if (res.data.success) {
        this.setState({ qrData: { ...res.data, booking_id: 'ALL', booking_ids, expires_at: Date.now() + 15 * 60 * 1000 } });
        const pollId = setInterval(async () => {
          try {
            const status = await axios.get('https://ridenrepair.com/api/settlement_qr_status', {
              params: { qr_id: res.data.qr_id },
              headers: authHeaders()
            });
            if (!status.data.paid && status.data.status === 'closed') {
              clearInterval(pollId);
              this.setState({ qrData: null, qrPolling: null });
              alert('This QR has expired and no payment was received. Please do not pay on it. Generate a new QR to pay your dues.');
              return;
            }
            if (status.data.paid) {
              clearInterval(pollId);
              this.recordPendingSettlements(booking_ids);
              // Settle all bookings via bulk endpoint
              try {
                let razorpay_payment_id = '';
                try {
                  const payments = await axios.get(`https://ridenrepair.com/api/settlement_qr_payments?qr_id=${res.data.qr_id}`, { headers: authHeaders() });
                  if (payments.data && payments.data.payment_id) razorpay_payment_id = payments.data.payment_id;
                } catch (e) {}
                await axios.post('https://ridenrepair.com/api/settle_multiple_bookings_cash', {
                  mechanic_phone: this.state.mechanic_phone,
                  booking_ids,
                  razorpay_payment_id,
                  total_amount: totalAmount
                }, { headers: authHeaders() });
              } catch (e) { console.log('Bulk settle after QR error:', e); }
              const settlementRes = await axios.get('https://ridenrepair.com/api/mechanic_settlement_bookings', {
                params: { mechanic_phone: this.state.mechanic_phone, grouped: true },
                headers: authHeaders()
              });
              this.setState({ qrData: null, qrPolling: null });
              this.setSettlementData(settlementRes.data);
            }
          } catch (e) {}
        }, 3000);
        this.setState({ qrPolling: pollId });
      }
    } catch (error) {
      console.log('Error creating settle-all QR:', error);
      alert(error.response && error.response.status === 409 ? this.paymentRefusedText(409) : 'Failed to generate QR code');
    }
  }

  closeQR = () => {
    if (this.state.qrPolling) clearInterval(this.state.qrPolling);
    this.setState({ qrData: null, qrPolling: null });
  }

  loadScript = (src) => {
		return new Promise((resolve) => {
			const script = document.createElement("script");
			script.src = src;
			script.onload = () => {
				resolve(true);
			};
			script.onerror = () => {
				resolve(false);
			};
			document.body.appendChild(script);
		});
	}

  assignWorker = (selectedWorker) => {
    const { selectedBooking } = this.state;

    if (!selectedBooking) {
      return;
    }

    const params = {
      booking_id: selectedBooking.booking_id,
      email: selectedBooking.email,
      worker_name: selectedWorker.worker_name,
      worker_number: selectedWorker.worker_number
    };
    
    axios.post(`https://ridenrepair.com/api/update_booking_worker_mechanic`, params, { headers: authHeaders() });
  }

  // 409 = ops already holds these dues in a settlement payment
  paymentRefusedText = (status) => (status === 409
    ? 'These dues are already being settled by Ride N Repair ops. Please do not pay in the app — contact ops.'
    : 'Could not start the payment. Please try again.');

  displayRazorpay = async (amount) => {
		const res = await this.loadScript(
			"https://checkout.razorpay.com/v1/checkout.js"
		);

		if (!res) {
			this.setState({ paymentToast: { type: 'error', text: 'Razorpay SDK failed to load. Check your network and retry.' } });
			return;
		}

		const settlementData = this.state.settlementData || {};
		const todayBookings = (settlementData.today || {}).unsettled_cash_bookings || [];
		const prevBookings = (settlementData.previous || {}).unsettled_cash_bookings || [];
		const allBookings = [...todayBookings, ...prevBookings];
		const booking_ids = allBookings.map((b) => b.booking_id);
		const isAggregate = booking_ids.length > 0;

		const reqBody = { amount: amount * 100, currency: 'INR' };
		if (isAggregate) {
			reqBody.mechanic_phone = this.state.mechanic_phone;
			reqBody.booking_ids = booking_ids;
			reqBody.purpose = 'aggregate_cash_settlement';
			if (allBookings[0] && allBookings[0].email) reqBody.email = allBookings[0].email;
		}

		const requestOptions = {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(reqBody)
		};

		const orderRes = await fetch('https://ridenrepair.com/api/razorpay', requestOptions);
		let data = null;
		try { data = await orderRes.json(); } catch (e) {}
		// No order ⇒ never open checkout (an order-less checkout charges the raw amount as paise, e.g. Rs.1229 → Rs.12.29)
		if (!orderRes.ok || !data || !data.id) {
			alert(this.paymentRefusedText(orderRes.status));
			return;
		}

		const options = {
			key: 'rzp_live_v9Y9ZhDUWFgjNI',
			amount: data.amount,
			currency: data.currency,
			name: 'Ride N Repair',
			description: 'Mechanic Payment',
				image: 'https://ridenrepair.com/api/logo.png',
				order_id: data.id,
				handler: (response) => {this.onSuccessfulPayment(data, response); },
				prefill: {
					name: this.state.mechanic_name,
					contact: this.state.mechanic_phone,
				},
				theme: {
					color: "#61dafb",
				},
				config: {
					display: {
						blocks: {
							upi: {
								name: 'Pay via UPI',
								instruments: [
									{ method: 'upi', flows: ['qr', 'collect', 'intent'] }
								]
							}
						},
						sequence: ['block.upi'],
						preferences: { show_default_blocks: true }
					}
				},
		};
		
		if (typeof window !== 'undefined') {
			const paymentObject = new window.Razorpay(options);
			paymentObject.open();
		}
	}

  render() {
    const {
      selectedDate,
      bookingsData,
      mechanic_name,
      mechanic_phone,
      selectedBooking,
      modalIsOpen,
      bookingConfirmations,
      commonParts, 
      allParts, 
      selectedPart, 
      selectedPartsList,
      selectedBookingNotes,
      notesSuccessMessage,
      uploadSuccessMessage,
      selectedPhotos,
      showConfirmationModal,
      isRequesting,
      requestStatus,
      mechanicStatus,
      walletBalance,
      mechanicInventory,
      submitNotesDisabled,
      selectedWorker,
      workerDetails,
      isWorker,
      totalBookingsCount,
      nearbyVendors,
      selectedVendor,
      settlementData
    } = this.state;

    const customStyles = {
      content: {
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        margin: '0',
        padding: '0',
        border: 'none',
        borderRadius: '0',
        background: '#f5f5f5',
        overflowY: 'auto',
        overflowX: 'hidden',
      },
      overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
      },
    };

    return (
      <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      <div className="mechanic-dashboard">
        {/* IMPL_06: language confirm (once) — brand palette black/#f42222 */}
        {this.state.showLangModal && this.state.wizardConfig && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#111', color: '#fff', borderRadius: 12, padding: 20,
                          width: '90%', maxWidth: 360, borderTop: '4px solid #f42222' }}>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>
                Apni bhasha chunein / Choose your language
              </div>
              <div style={{ fontSize: 13, color: '#bbb', marginBottom: 14 }}>
                Calls aur updates isi bhasha mein aayenge.
              </div>
              {[['hi', 'हिन्दी'], ['ta', 'தமிழ்'], ['te', 'తెలుగు'], ['mr', 'मराठी'],
                ['bn', 'বাংলা'], ['kn', 'ಕನ್ನಡ'], ['en', 'English']].map(([code, label]) => (
                <button key={code} onClick={() => this.saveLanguage(code)}
                  style={{ display: 'inline-block', margin: 4, padding: '10px 16px', fontSize: 15,
                           background: (this.state.wizardConfig.language === code) ? '#f42222' : '#222',
                           color: '#fff', border: '1px solid #444', borderRadius: 8 }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Profile basics (once, all mechanics): language + T-shirt size in one form */}
        {this.state.showProfileBasics && this.state.wizardConfig && (
          <MechanicProfileBasicsModal
            currentLanguage={this.state.wizardConfig.language}
            currentSize={this.state.wizardConfig.tshirt_size}
            onSaved={this.onProfileBasicsSaved}
            onLater={() => this.setState({ showProfileBasics: false })}
          />
        )}
        {/* IMPL_06: tier badge strip */}
        {this.state.wizardConfig && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px' }}>
            <span style={{ background: '#111', color: '#fff', borderRadius: 12, padding: '3px 12px',
                           fontSize: 12, fontWeight: 700, border: '1px solid #f42222' }}>
              {(this.state.wizardConfig.tier || 'bronze').toUpperCase()} MECHANIC
            </span>
            {this.state.wizardConfig.language && (
              <span style={{ color: '#666', fontSize: 12 }}>
                {String(this.state.wizardConfig.language).toUpperCase()}
              </span>
            )}
          </div>
        )}
        {/* IMPL_07: spare-QR scan -> ₹30 coupon credit */}
        <SpareScanCard getToken={() => getLocalItem('authToken')} />
        {this.state.enforcementState && (
          <TrainingEnforcementModal
            enforcementState={this.state.enforcementState}
            preferredLang={this.state.enforcementLang}
            mechanicPhone={this.state.mechanic_phone}
            onDismiss={() => this.setState({ enforcementState: null })}
          />
        )}
        <header>
          <h2>Hello {mechanic_name}</h2>
          <button className="logout-button" onClick={this.handleLogout}>
            Logout
          </button>
        </header>
        
        {/* QR Code Full-Screen Overlay */}
        {this.state.qrData && (
            <div className="qr-overlay" onClick={this.closeQR}>
                <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
                    <p className="qr-amount">Rs. {this.state.qrData.amount}</p>
                    <p className="qr-booking">{this.state.qrData.booking_id === 'ALL' ? 'All pending dues' : `Booking #${this.state.qrData.booking_id}`}</p>
                    <img src={this.state.qrData.image_url} alt="UPI QR Code" className="qr-image" />
                    <p className="qr-hint">Scan from any UPI app</p>
                    {this.state.qrData.expires_at && (
                        <p className="qr-hint">Valid till {new Date(this.state.qrData.expires_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} — pay before this time</p>
                    )}
                    <div className="qr-waiting-row"><span className="qr-dot"></span> Waiting for payment...</div>
                    <button className="qr-close-btn" onClick={this.closeQR}>Close</button>
                </div>
            </div>
        )}

        {/* Quick Stats Cards */}
        <div className="quick-stats">
          {/* Settlement Summary Card */}
          {settlementData && (settlementData.totals || {}).total_mechanic_owes_rnr > 0 && (
              <div className="stat-card settlement-card">
                  <div className="stat-label">Cash Dues</div>
                  <div className="settlement-row owe">
                      <span>Total Due:</span>
                      <span className="amount">Rs. {(settlementData.totals || {}).total_mechanic_owes_rnr}</span>
                  </div>
                  {[...((settlementData.today || {}).unsettled_cash_bookings || []), ...((settlementData.previous || {}).unsettled_cash_bookings || [])].some(b => b.settlement_lock) ? (
                      <div className="settlement-row" style={{ color: '#92400e', fontSize: '13px' }}>
                          Being settled by Ride N Repair ops — no payment needed here.
                      </div>
                  ) : (
                  <div className="settle-actions">
                      <button
                          className="settle-all-btn"
                          onClick={() => this.displayRazorpay((settlementData.totals || {}).total_mechanic_owes_rnr)}
                      >
                          Pay All — Rs. {(settlementData.totals || {}).total_mechanic_owes_rnr}
                      </button>
                      <button
                          className="settle-all-btn qr-btn"
                          onClick={() => this.showSettleAllQR()}
                      >
                          QR Code
                      </button>
                  </div>
                  )}
              </div>
          )}

          {/* Keep old wallet balance card for backwards compatibility with existing bookings */}
          {walletBalance < 0 && (
              <div className="stat-card wallet-card">
                  <div className="stat-label">Legacy Wallet Balance</div>
                  <div className="stat-value">Rs. {walletBalance}</div>
                  <button
                    className="logout-button"
                    style={{ marginTop: '0.5rem', fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                    onClick={() => this.displayRazorpay(parseFloat(walletBalance) * -1)}
                  >
                      Pay Rs. {parseFloat(walletBalance) * -1}
                  </button>
              </div>
          )}

          <div className="stat-card">
            <div className="stat-label">Today's Bookings</div>
            <div className="stat-value">{totalBookingsCount}</div>
          </div>
        </div>

        {/* Inventory Section */}
        {mechanicInventory && 
          <div className="inventory-wrapper">
            <h5 className="mb-3">Mechanic Inventory</h5>
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Part Name</th>
                  <th>Qty</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(mechanicInventory).map(([name, { quantity, costPrice }], i) => (
                  <tr key={name}>
                    <td>{i + 1}</td>
                    <td>{name}</td>
                    <td>{quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }

        {/* Action Sections */}
        <div className="action-section">
          <div className="section-title">Quick Actions</div>
          <div className="action-buttons single-button">
            <button className="logout-button" onClick={this.fetchBookingsData}>
              🔄 Get My Bookings
            </button>
          </div>
        </div>

        {/* Status Section */}
        <div className="action-section">
          <div className="section-title">Current Status</div>
          <div className="status-indicator">
            <span className={`status-dot ${mechanicStatus === 'BUSY' ? 'busy' : mechanicStatus === 'AVAILABLE' ? '' : 'unavailable'}`}></span>
            <span className="status-text">{mechanicStatus}</span>
          </div>
          <div className="action-buttons status-buttons">
            <button className="logout-button success" onClick={() => this.updateMechanicStatus('AVAILABLE')}>
              ✅ Available
            </button>
            <button className="logout-button danger" onClick={() => this.updateMechanicStatus('BUSY')}>
              ⏸️ Busy
            </button>
          </div>
        </div>

        {/* Monthly Stats */}
        {this.state.monthlyStats && (
          <div className="monthly-stats">
            <h6>My Monthly Stats</h6>
            <div className="stat-item">
              <span className="stat-label">Total Bookings</span>
              <span className="stat-value">{this.state.monthlyStats.total_bookings}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Completions</span>
              <span className="stat-value">{this.state.monthlyStats.total_completions}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Avg Ticket Size</span>
              <span className="stat-value">Rs. {this.state.monthlyStats.average_ticket_size}</span>
            </div>
          </div>
        )}

        {/* Today's Cash Dues */}
        {settlementData && (settlementData.today || {}).unsettled_cash_bookings && settlementData.today.unsettled_cash_bookings.length > 0 && (
            <div className="settlement-section">
                <h3>Today's Cash Dues</h3>
                {settlementData.today.unsettled_cash_bookings.map((booking) => (
                    <div key={booking.booking_id} className="settlement-booking-card">
                        <div className="settlement-booking-header">
                            <span className="booking-id">#{booking.booking_id}</span>
                            <span className="service-type">{booking.service_type}</span>
                        </div>
                        <div className="settlement-details">
                            <div className="detail-row">
                                <span>Total Collected:</span>
                                <span>Rs. {booking.final_payment_amount}</span>
                            </div>
                            <div className="detail-row">
                                <span>Your Payout (labor):</span>
                                <span>Rs. {booking.mechanic_payout}</span>
                            </div>
                            <div className="detail-row highlight">
                                <span>Due to Platform:</span>
                                <span>Rs. {booking.settlement_amount}</span>
                            </div>
                        </div>
                        {booking.settlement_lock ? (
                            <div className="detail-row" style={{ color: '#92400e', fontSize: '13px' }}>
                                Being settled by Ride N Repair ops — no payment needed here.
                            </div>
                        ) : (
                        <div className="settle-actions">
                            <button
                                className="settle-btn"
                                disabled={this.state.settlingBookingId === booking.booking_id}
                                onClick={() => this.settleBooking(booking)}
                            >
                                {this.state.settlingBookingId === booking.booking_id ? 'Settling...' : `Pay Rs. ${booking.settlement_amount}`}
                            </button>
                            <button
                                className="settle-btn qr-btn"
                                onClick={() => this.showSettlementQR(booking)}
                            >
                                QR Code
                            </button>
                        </div>
                        )}
                    </div>
                ))}
            </div>
        )}

        {/* Previous Unsettled */}
        {settlementData && (settlementData.previous || {}).unsettled_cash_bookings && settlementData.previous.unsettled_cash_bookings.length > 0 && (
            <div className="settlement-section">
                <h3
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                    onClick={() => this.setState({ previousExpanded: !this.state.previousExpanded })}
                >
                    Previous Unsettled
                    <span className="previous-badge">{settlementData.previous.unsettled_cash_bookings.length}</span>
                    <span style={{ fontSize: '14px' }}>{this.state.previousExpanded ? '\u25B2' : '\u25BC'}</span>
                </h3>
                {this.state.previousExpanded && settlementData.previous.unsettled_cash_bookings.map((booking) => (
                    <div key={booking.booking_id} className="settlement-booking-card">
                        <div className="settlement-booking-header">
                            <span className="booking-id">#{booking.booking_id}</span>
                            <span className="service-type">{booking.service_type}</span>
                        </div>
                        <div className="settlement-booking-date">{booking.date_of_service}</div>
                        <div className="settlement-details">
                            <div className="detail-row">
                                <span>Total Collected:</span>
                                <span>Rs. {booking.final_payment_amount}</span>
                            </div>
                            <div className="detail-row">
                                <span>Your Payout (labor):</span>
                                <span>Rs. {booking.mechanic_payout}</span>
                            </div>
                            <div className="detail-row highlight">
                                <span>Due to Platform:</span>
                                <span>Rs. {booking.settlement_amount}</span>
                            </div>
                        </div>
                        {booking.settlement_lock ? (
                            <div className="detail-row" style={{ color: '#92400e', fontSize: '13px' }}>
                                Being settled by Ride N Repair ops — no payment needed here.
                            </div>
                        ) : (
                        <div className="settle-actions">
                            <button
                                className="settle-btn"
                                disabled={this.state.settlingBookingId === booking.booking_id}
                                onClick={() => this.settleBooking(booking)}
                            >
                                {this.state.settlingBookingId === booking.booking_id ? 'Settling...' : `Pay Rs. ${booking.settlement_amount}`}
                            </button>
                            <button
                                className="settle-btn qr-btn"
                                onClick={() => this.showSettlementQR(booking)}
                            >
                                QR Code
                            </button>
                        </div>
                        )}
                    </div>
                ))}
                {!this.state.previousExpanded && (
                    <div className="previous-summary" onClick={() => this.setState({ previousExpanded: true })}>
                        Rs. {settlementData.previous.total_cash_owed} from {settlementData.previous.unsettled_cash_bookings.length} booking(s) — tap to view
                    </div>
                )}
            </div>
        )}

        {/* Pending Cost Prices */}
        {settlementData && settlementData.pending_cost_prices && settlementData.pending_cost_prices.length > 0 && (
            <div className="settlement-section">
                <h3>Awaiting Cost Prices</h3>
                {settlementData.pending_cost_prices.map((booking) => (
                    <div key={booking.booking_id} className="settlement-booking-card pending">
                        <span className="booking-id">#{booking.booking_id}</span>
                        <span className="pending-label">Awaiting cost prices from ops</span>
                    </div>
                ))}
            </div>
        )}


        {/* Request New Booking — hidden for now */}
        {/*
        {!isWorker && (
          <div className="action-section">
            <div className="action-buttons single-button">
              <button
                className="logout-button"
                onClick={this.requestNewBooking}
                disabled={isRequesting}
              >
                {isRequesting ? 'Requesting...' : 'Request New Booking'}
              </button>
            </div>
            {requestStatus && <div className={`message ${requestStatus.includes('successfully') ? 'success' : 'info'}`}>{requestStatus}</div>}
          </div>
        )}
        */}

        {/* Date Picker */}
        <div className="date-div">
          <span>Booking Date:</span>
          <DatePicker 
            className="form-control"
            value={selectedDate}
            onChange={this.handleChangeDate}
            showYearDropdown
          />
        </div>

        {/* Bookings List */}
        {bookingsData.map((booking) => {
          const statusClass = booking.order_status === 'COMPLETED' || booking.order_status === 'CASH TAKEN' || booking.order_status === 'SETTLED'
            ? 'completed'
            : booking.order_status.includes('IN PROGRESS') || booking.order_status.includes('STARTED')
            ? 'in-progress'
            : CANCELLED_STATUSES.includes(booking.order_status)
            ? 'cancelled'
            : 'pending';
          
          return (
            <div
              key={booking.booking_id}
              className="booking-card"
              role="button"
              tabIndex={0}
              onClick={() => this.handleBookingClick(booking.booking_id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  this.handleBookingClick(booking.booking_id);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <p
                className="booking-id"
              >
                Booking ID: {booking.booking_id}
              </p>
              <div className="service-type">{booking.service_type}</div>
              <div className="booking-info">
                <div className="info-item">
                  <span className="info-label">Customer</span>
                  <span className="info-value">{booking.first_name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Date</span>
                  <span className="info-value">{booking.date_of_service}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Time</span>
                  <span className="info-value">{booking.time_of_service}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Status</span>
                  <span className={`order-status ${statusClass}`}>{booking.order_status}</span>
                </div>
              </div>
              {booking.final_payment_amount && (
                <div className="total-amount">
                  Total: Rs. {Math.round(parseInt(booking.final_payment_amount))}
                </div>
              )}
            </div>
          );
        })}
        {selectedBooking && (
          <Modal
            isOpen={modalIsOpen}
            onRequestClose={this.closeModal}
            style={customStyles}
            contentLabel="Booking Details"
            className="ReactModal__Content"
            overlayClassName="ReactModal__Overlay"
          >
            <div className="modal-content-wrapper">
              <div className="modal-header">
                <h3>Booking Details</h3>
                <button className="close-button" onClick={this.closeModal}>×</button>
              </div>
              
              <div className="modal-section">
                <div className="section-title">Basic Information</div>
                <p><strong>Booking ID:</strong> {selectedBooking.booking_id}</p>
                <p><strong>Date:</strong> {selectedBooking.date_of_service}</p>
                <p><strong>Time:</strong> {selectedBooking.time_of_service}</p>
                <p><strong>Status:</strong> <span className={`order-status ${selectedBooking.order_status === 'COMPLETED' || selectedBooking.order_status === 'CASH TAKEN' || selectedBooking.order_status === 'SETTLED' ? 'completed' : selectedBooking.order_status.includes('IN PROGRESS') || selectedBooking.order_status.includes('STARTED') ? 'in-progress' : CANCELLED_STATUSES.includes(selectedBooking.order_status) ? 'cancelled' : 'pending'}`}>{selectedBooking.order_status}</span></p>
                <p><strong>Vehicle:</strong> {selectedBooking.vehicle_name}</p>
                <p><strong>Service Type:</strong> {selectedBooking.service_type}</p>
              </div>

              <div className="modal-section">
                <div className="section-title">Vehicle Details</div>
                <label>Vehicle Make</label>
                <select
                  value={selectedBooking.vehicle_make || ''}
                  onChange={this.handleVehicleMakeChange}
                >
                  <option value="">-- Select Make --</option>
                  {(() => {
                    let type = 'car';
                    if (selectedBooking.vehicle_category && typeof selectedBooking.vehicle_category === 'string') {
                      type = selectedBooking.vehicle_category.toLowerCase() === 'bike' ? 'bike' : 'car';
                    }
                    const makesList = type === 'car'
                      ? this.state.vehicleMakesCar
                      : this.state.vehicleMakesBike;
                    return makesList.map(mk => (
                      <option key={mk.value} value={mk.value}>{mk.label}</option>
                    ));
                  })()}
                </select>
                <label>Vehicle Model</label>
                <select
                  value={selectedBooking.vehicle_model || ''}
                  onChange={this.handleVehicleModelChange}
                  disabled={!selectedBooking.vehicle_make}
                >
                  <option value="">-- Select Model --</option>
                  {(() => {
                    const make = selectedBooking.vehicle_make;
                    const modelsList = make && this.state.vehicleModels[make]
                      ? this.state.vehicleModels[make]
                      : [];
                    return modelsList.map(mo => (
                      <option key={mo.value} value={mo.value}>{mo.label}</option>
                    ));
                  })()}
                </select>
                <label>Vehicle Year</label>
                <select
                  value={selectedBooking.vehicle_year || ''}
                  onChange={this.handleVehicleYearChange}
                >
                  <option value="">-- Select Year --</option>
                  {this.state.vehicleYears.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              {!TERMINAL_STATUSES.includes(selectedBooking.order_status) && (
                <>
                  <div className="modal-section">
                    <div className="section-title">Customer Information</div>
                    <p><strong>Name:</strong> {selectedBooking.first_name}</p>
                    <p><strong>Address:</strong> {selectedBooking.address}</p>
                    <a
                      className="map-link-button"
                      href={`https://www.google.com/maps/dir/?api=1&destination=${selectedBooking.latitude},${selectedBooking.longitude}&travelmode=driving`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open Customer Location on Maps
                    </a>
                  </div>

                  {selectedBooking.order_status !== 'SERVICE STARTED' && (
                    <div className="modal-section otp-section">
                      <div className="section-title">Start Service</div>
                      {!this.state.isOtpSent ? (
                        <button
                          className="logout-button full-width"
                          onClick={this.sendOtpToCustomer}
                          disabled={this.state.isSendingOtp}
                        >
                          {this.state.isSendingOtp ? '⏳ Sending OTP...' : '📱 Confirm Start Service'}
                        </button>
                      ) : (
                        <div>
                          <div className={`otp-message ${this.state.otpVerificationMessage.includes('sent') ? 'success' : 'info'}`}>
                            {this.state.otpVerificationMessage}
                          </div>
                          <div className="otp-input-wrapper">
                            <input
                              type="text"
                              placeholder="Enter OTP"
                              value={this.state.enteredOtp}
                              onChange={(e) => this.setState({ enteredOtp: e.target.value })}
                              maxLength="6"
                            />
                            <button
                              className="logout-button"
                              onClick={this.verifyOtp}
                            >
                              ✅ Start Service
                            </button>
                          </div>
                          {this.state.isOtpVerified && (
                            <div className="otp-message success">Service Started Successfully!</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {workerDetails.length > 0 && (
                    <div className="modal-section">
                      <div className="section-title">Assign Worker</div>
                      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                        <select
                          id="worker-select"
                          value={selectedWorker.worker_number || ""}
                          onChange={(e) => {
                            const selectedOption = workerDetails.find(worker => worker.phone === e.target.value);
                            this.setState({
                              selectedWorker: selectedOption ? {
                                worker_number: selectedOption.phone,
                                worker_name: selectedOption.name
                              } : {}
                            });
                          }}
                          style={{ flex: 1 }}
                        >
                          <option value="" disabled>Select a worker</option>
                          {workerDetails.map((worker) => (
                            <option key={worker.phone} value={worker.phone}>
                              {worker.name}
                            </option>
                          ))}
                        </select>
                        <button
                          className="logout-button"
                          onClick={() => this.assignWorker(selectedWorker)}
                          disabled={!selectedWorker.worker_number}
                          style={{ minWidth: '120px' }}
                        >
                          Assign
                        </button>
                      </div>
                    </div>
                  )}

                  {/* IMPL_06: enforced-wizard step trail (pilot mechanics only; grid untouched) */}
                  {this.state.wizardConfig && this.state.wizardConfig.wizard_pilot && (
                    <div className="modal-section">
                      <div className="section-title">Job Progress</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '6px 0' }}>
                        {['ACCEPTED', 'EN_ROUTE', 'REACHED', 'INSPECT', 'ESTIMATE', 'APPROVE',
                          'SERVICE', 'PHOTOS', 'INVOICE', 'COLLECT', 'COMPLETE'].map((s, i, arr) => {
                          const cur = (selectedBooking && selectedBooking.job_step) || 'ACCEPTED';
                          const curIdx = arr.indexOf(cur);
                          const isCur = i === curIdx;
                          const done = i < curIdx;
                          const isNext = i === curIdx + 1 && s !== 'COMPLETE';
                          return (
                            <span key={s}
                              onClick={isNext ? async () => {
                                try {
                                  const r = await axios.post('https://ridenrepair.com/api/advance_job_step',
                                    { booking_id: selectedBooking.booking_id, next_step: s,
                                      mechanic_phone: this.state.mechanic_phone },
                                    { headers: authHeaders() });
                                  if (r.data && r.data.status === 'ok') {
                                    this.setState(prev => ({
                                      selectedBooking: { ...prev.selectedBooking, job_step: s },
                                    }));
                                  }
                                } catch (e) {
                                  alert((e.response && e.response.data && e.response.data.error) || 'Step not allowed yet');
                                }
                              } : undefined}
                              style={{ padding: '5px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                                       background: isCur ? '#f42222' : done ? '#111' : '#eee',
                                       color: isCur || done ? '#fff' : '#999',
                                       border: isNext ? '2px solid #f42222' : '1px solid #ddd',
                                       cursor: isNext ? 'pointer' : 'default' }}>
                              {done ? '✓ ' : ''}{s.replace('_', ' ')}{isNext ? ' →' : ''}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="modal-section">
                    <div className="section-title">Quick Actions</div>
                    <div className="action-buttons-grid">
                      <button className="logout-button" onClick={() => this.callCustomer()}>
                        📞 Call Customer
                      </button>
                      <button className="logout-button success" onClick={() => { this.updateBookingStatus('IN PROGRESS'); }}>
                        ✅ Going to Customer
                      </button>
                      <button className="logout-button" onClick={() => { this.updateBookingStatus('NO RESPONSE'); }}>
                        ❌ No Response
                      </button>
                      <button className="logout-button" onClick={() => { this.updateBookingStatus('MECHANIC REACHED'); }}>
                        🏁 Reached
                      </button>
                      {selectedBooking.charges_desc && selectedBooking.amount_details &&
                       selectedBooking.order_status !== 'COMPLETED' && selectedBooking.order_status !== 'SETTLED' && (
                        <button className={`logout-button${this.state.disableInvoiceButton ? ' disabled-button' : ''}`} onClick={this.regeneratePaymentLink}>
                          🔄 Regenerate Payment Link
                        </button>
                      )}
                      {/* <button className="logout-button" onClick={() => { this.updateBookingStatus('IN PROGRESS - TOWED'); }}>
                        🚛 Towed
                      </button>
                      <button className="logout-button success" onClick={() => this.updateBookingStatus('CASH TAKEN')}>
                        💵 Cash Taken
                      </button> */}
                    </div>
                  </div>

                  <div className="modal-section notes-section">
                    <div className="section-title">Add Notes</div>
                    <div className="notes-input-wrapper">
                      <input 
                        type="text" 
                        name="mechanic_notes" 
                        value={selectedBookingNotes} 
                        placeholder='Enter notes here...' 
                        onChange={this.handleNotesChange} 
                        disabled={submitNotesDisabled}
                      />
                      <button className="logout-button" onClick={this.submitNotesUpdate} disabled={submitNotesDisabled}>
                        Save
                      </button>
                    </div>
                    {notesSuccessMessage && <div className="notes-success">{notesSuccessMessage}</div>}
                  </div>

                  {nearbyVendors && nearbyVendors.length > 0 && (
                    <div className="modal-section vendor-select">
                      <div className="section-title">Spare Parts Vendor</div>
                      <select
                        value={selectedVendor ? selectedVendor.phone : ''}
                        onChange={(e) => {
                          const selectedPhone = e.target.value;
                          const selectedVendor = nearbyVendors.find(
                            (vendor) => vendor.phone === selectedPhone
                          );
                          this.setState({ selectedVendor });
                        }}
                      >
                        <option value="">Select a vendor</option>
                        {nearbyVendors.map((vendor) => (
                          <option key={vendor.phone} value={vendor.phone}>
                            {vendor.name} - {vendor.distance.toFixed(2)} km away
                          </option>
                        ))}
                      </select>
                      {selectedVendor && (
                        <div className="vendor-actions">
                          <button
                            className="logout-button"
                            onClick={this.callVendor}
                          >
                            📞 Call {selectedVendor.name}
                          </button>
                          <button
                            className="logout-button"
                            onClick={this.bookPorter}
                            disabled={this.state.isBookingPorter}
                          >
                            {this.state.isBookingPorter ? '⏳ Booking...' : '🚚 Book Porter'}
                          </button>
                        </div>
                      )}
                      {this.state.porterBookingMessage && (
                        <div className={`message ${this.state.porterBookingMessage.includes('successfully') ? 'success' : 'info'}`}>
                          {this.state.porterBookingMessage}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="modal-section photo-upload-section">
                    <div className="section-title">Upload Photos</div>
                    <div className="upload-button-wrapper">
                      <input type="file" multiple onChange={this.handlePhotoUpload} />
                      <div className="upload-button-label">
                        📷 Tap to Select Photos
                      </div>
                    </div>
                    {selectedPhotos.length > 0 && (
                      <div className="selected-photos-list">
                        <h5>Selected Photos ({selectedPhotos.length}):</h5>
                        <ul>
                          {selectedPhotos.map((file, index) => (
                            <li key={index}>{file.name}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <button className="logout-button full-width" onClick={this.uploadPhotos} disabled={selectedPhotos.length === 0}>
                      {selectedPhotos.length > 0 ? `📤 Upload ${selectedPhotos.length} Photo(s)` : 'Select Photos First'}
                    </button>
                    {uploadSuccessMessage && (
                      <div className={`message ${uploadSuccessMessage.includes('successfully') ? 'success' : 'error'}`}>
                        {uploadSuccessMessage}
                      </div>
                    )}
                  </div>
                </>
              )}

                  {/* <div>
                    <div>
                      <h6>Common Parts:</h6>
                      {commonParts && commonParts.map((part) => (
                        <button key={part.part_id} style={{ 'margin': '5px' }} onClick={() => this.handleClickPart(part)}>
                          {part.part_name}
                        </button>
                      ))}
                      <button style={{ 'margin': '5px' }} onClick={this.handleAddOtherPart}>
                        Other
                      </button>
                    </div>
                    <br/>
                    <div>
                      <h6>All Parts:</h6>
                      <select value={selectedPart} onChange={this.handleSelectPart}>
                        <option value="">Select a part</option>
                        {allParts && allParts.map((part) => (
                          <option 
                            key={part.part_id} 
                            data-part-id={part.part_id}
                            data-part-name={part.part_name}
                            value={part.part_name}
                          >
                            {part.part_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <br/>
                    {selectedPartsList.length > 0 && (
                      <div>
                        <h6>Selected Parts:</h6>
                        <div className='table-container'>
                          <table className='part-table'>
                            <thead>
                              <tr>
                                <th>Part</th>
                                <th>Price</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedPartsList.map((selectedPart, index) => (
                                <tr key={index} style={{ 'border-bottom': '1px solid #ccc' }}>
                                  <td style={{ 'padding': '8px' }}>
                                    <input
                                      type="text"
                                      placeholder="Enter Part Name"
                                      value={selectedPart.partName}
                                      style={{ minWidth: '150px', width: `${Math.max(selectedPart.partName.length * 8, 150)}px` }}
                                      onChange={(e) => this.handleOtherPartNameChange(index, e.target.value)}
                                    />
                                  </td>
                                  <td style={{ 'padding': '8px' }}>
                                    <input
                                      type="number"
                                      placeholder="Sales Price"
                                      value={selectedPart.salesPrice}
                                      onChange={(e) => this.handleSalesPriceChange(index, e.target.value)}
                                      onKeyDown={(e) => {
                                        // Prevent typing of minus sign if input value is empty or already negative
                                        if (e.key === '-' && (!selectedPart.salesPrice || selectedPart.salesPrice.startsWith('-'))) {
                                          e.preventDefault();
                                        }
                                      }}
                                    />
                                  </td>
                                  <td style={{ 'padding': '8px' }}>
                                    <button className="logout-button" style={{ 'padding': '5px 5px' }} onClick={() => this.removePart(index)}>
                                      Remove
                                    </button>
                                  </td>
                                </tr>
                              ))}
                              <tr>
                                <td colSpan="3" style={{ textAlign: 'right', paddingRight: '8px' }}>Total Amount:</td>
                                <td style={{ padding: '8px' }}>
                                  {selectedPartsList.reduce((total, part) => total + parseFloat(part.salesPrice || 0), 0)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                  <br/>
                  {this.state.invoiceMessage || this.state.completedMessage}
                  <center>
                    <button className={`logout-button${this.state.disableInvoiceButton ? ' disabled-button' : ''}`} style={{ 'margin': '5px', 'padding': '5px 5px' }} onClick={this.generateInvoice}>
                      Generate Invoice
                    </button>
                  </center> */}

              {/* Display additional details for COMPLETED bookings */}
              {(selectedBooking.order_status === 'COMPLETED' || selectedBooking.order_status === 'CASH TAKEN' || selectedBooking.order_status === 'SETTLED') && (
                <div className="modal-section">
                  <div className="section-title">Completed Details</div>
                  {selectedBooking.charges_desc && (
                    <div>
                      <p><strong>Charges:</strong></p>
                      <ul style={{ listStyle: 'none', padding: 0, margin: '0.75rem 0' }}>
                        {selectedBooking.charges_desc.split(',').map((item, index) => (
                          <li key={index} style={{ padding: '0.5rem', background: '#f8f9fa', marginBottom: '0.5rem', borderRadius: '8px' }}>
                            <strong>{item}:</strong> Rs. {(parseFloat(selectedBooking.amount_details.split(',')[index])).toFixed(1)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p><strong>Completed On:</strong> {this.formatDate(selectedBooking.completed_on)}</p>
                </div>
              )}

              <div className="modal-section">
                <button className="logout-button full-width" onClick={this.closeModal}>
                  ✕ Close
                </button>
              </div>
            </div>
          </Modal>
        )}

        {/* Confirmation Modal */}
        {showConfirmationModal && selectedBooking && !bookingConfirmations[selectedBooking.booking_id] && !['IN PROGRESS', 'IN PROGRESS - AUTO', 'IN PROGRESS - VEHICLE TOWED', 'IN PROGRESS - BATTERY TOWED'].includes(selectedBooking.order_status) && (
          ReactDOM.createPortal(
            <div className="confirmation-modal">
              <div>
                <h3>Confirm Booking</h3>
                <p>Are you going to this booking?</p>
                <div className="confirmation-buttons">
                  <button onClick={() => { this.acceptBookingFromWebView(); this.closeConfirmationModal(true); }}>
                    ✅ Yes, I'm Going
                  </button>
                  <button onClick={() => this.handleBookingDenial()}>
                    ❌ No, Give to Another
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        )}
      </div>
      </>
    );
  }
}

export default MechanicDashboard;
